import React from "react";
import { Link } from "react-router-dom";
import { AppCard, AppPage, AppPageHeader } from "../../components/common/appShell";

import "./teamRoles.css";

const ROLE_PERMISSION_CATALOG = [
  { field: "viewDeviceInfo", label: "View devices" },
  { field: "editDeviceInfo", label: "Edit devices", control: true },
  { field: "viewInletInfo", label: "View inlets" },
  { field: "editInletInfo", label: "Edit inlets", control: true },
  { field: "viewSensorInfo", label: "View sensors" },
  { field: "editSensorInfo", label: "Edit sensors", control: true },
  { field: "viewAllOutletsAllowed", label: "View outlets" },
  { field: "editAllOutletsAllowed", label: "Edit outlets", control: true },
  { field: "switchAllOutletsAllowed", label: "Switch outlets", control: true },
  { field: "viewActivityLog", label: "View activity" },
  { field: "viewEventInfo", label: "View events" },
  { field: "editEventInfo", label: "Manage events", control: true },
];

function getEnabledPermissions(role = {}) {
  return ROLE_PERMISSION_CATALOG.filter((permission) => role[permission.field] === true);
}

function hasControlAccess(role = {}) {
  return ROLE_PERMISSION_CATALOG.some(
    (permission) => permission.control && role[permission.field] === true,
  );
}

function getAccessProfile(role = {}) {
  const enabledPermissions = getEnabledPermissions(role);
  if (enabledPermissions.length === 0) {
    return {
      className: "roles-status roles-status--empty",
      label: "No grants",
    };
  }

  if (hasControlAccess(role)) {
    return {
      className: "roles-status roles-status--control",
      label: "Control access",
    };
  }

  return {
    className: "roles-status roles-status--view",
    label: "Read access",
  };
}

export default class UserRoles extends React.Component {
  componentDidMount() {
    if (
      !this.props.resources.rolesLoaded &&
      !this.props.resources.rolesLoading
    ) {
      this.props.fetchResource("roles");
    }
  }

  renderPermissionChips(role) {
    const enabledPermissions = getEnabledPermissions(role);
    const visiblePermissions = enabledPermissions.slice(0, 4);
    const hiddenPermissionCount = enabledPermissions.length - visiblePermissions.length;

    if (enabledPermissions.length === 0) {
      return <span className="roles-muted">No explicit permissions enabled</span>;
    }

    return (
      <div className="roles-permission-chips">
        {visiblePermissions.map((permission) => (
          <span className="roles-permission-chip" key={permission.field}>
            {permission.label}
          </span>
        ))}
        {hiddenPermissionCount > 0 ? (
          <span className="roles-permission-chip roles-permission-chip--more">
            +{hiddenPermissionCount} more
          </span>
        ) : null}
      </div>
    );
  }

  render() {
    const roles = Array.isArray(this.props.resources.roles)
      ? this.props.resources.roles
      : [];
    const totalPermissionGrants = roles.reduce(
      (sum, role) => sum + getEnabledPermissions(role).length,
      0,
    );
    const controlRoleCount = roles.filter((role) => hasControlAccess(role)).length;

    return (
      <AppPage className="roles-page">
        <AppPageHeader
          // eyebrow="Team"
          title="Roles"
          description="Create reusable permission sets for team members and device groups."
          actions={
            <Link to="/team/roles/new" className="button roles-button-link">
              <span className="material-icons roles-button-icon" aria-hidden="true">
                add
              </span>
              Create Role
            </Link>
          }
        />

        <div className="app-kpi-grid">
          <div className="app-kpi-card">
            <div className="app-kpi-card__value">{roles.length}</div>
            <div className="app-kpi-card__label">Roles</div>
            <div className="app-kpi-card__meta">Reusable permission sets</div>
            <div className="app-kpi-card__accent" />
          </div>
          <div className="app-kpi-card">
            <div className="app-kpi-card__value">{controlRoleCount}</div>
            <div className="app-kpi-card__label">Control Roles</div>
            <div className="app-kpi-card__meta">Can edit, switch, or manage events</div>
            <div className="app-kpi-card__accent" />
          </div>
          <div className="app-kpi-card">
            <div className="app-kpi-card__value">{totalPermissionGrants}</div>
            <div className="app-kpi-card__label">Permission Grants</div>
            <div className="app-kpi-card__meta">Enabled across all roles</div>
            <div className="app-kpi-card__accent" />
          </div>
        </div>

        <section className="roles-assignment-note">
          <span className="material-icons roles-assignment-note__icon" aria-hidden="true">
            info
          </span>
          <div className="roles-assignment-note__copy">
            <h3>How role assignments work</h3>
            <p>
              Device group roles are scoped to the devices in that group. Individual
              roles apply directly to one user across their accessible devices.
              Assignments are additive, so multiple roles combine into one effective
              permission set.
            </p>
          </div>
        </section>

        <AppCard flush>
          {roles.length > 0 ? (
            <div className="roles-table-wrap">
              <table className="roles-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Access Profile</th>
                    <th>Enabled Permissions</th>
                    <th>Grants</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => {
                    const accessProfile = getAccessProfile(role);
                    const grantCount = getEnabledPermissions(role).length;

                    return (
                      <tr key={role.id}>
                        <td>
                          <Link to={`/team/roles/${role.id}`} className="roles-title-link">
                            {role.name || "Untitled role"}
                          </Link>
                          <div className="roles-muted">
                            {role.description || "No description provided."}
                          </div>
                        </td>
                        <td>
                          <span className={accessProfile.className}>
                            {accessProfile.label}
                          </span>
                        </td>
                        <td>{this.renderPermissionChips(role)}</td>
                        <td>
                          <span className="roles-grant-count">{grantCount}</span>
                          <div className="roles-muted">
                            {grantCount === 1 ? "permission" : "permissions"}
                          </div>
                        </td>
                        <td>
                          <div className="roles-row-actions">
                            <Link
                              to={`/team/roles/${role.id}`}
                              className="button secondary roles-compact-button"
                            >
                              <span
                                className="material-icons roles-button-icon"
                                aria-hidden="true"
                              >
                                edit
                              </span>
                              Edit
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="roles-empty-state">
              <span className="material-icons roles-empty-state__icon" aria-hidden="true">
                security
              </span>
              <h3>No roles yet</h3>
              <p>
                Create a permission set once, then assign it to team members or device
                groups as your access model grows.
              </p>
              <Link to="/team/roles/new" className="button roles-button-link">
                <span className="material-icons roles-button-icon" aria-hidden="true">
                  add
                </span>
                Create Role
              </Link>
            </div>
          )}
        </AppCard>
      </AppPage>
    );
  }
}
